<script lang="ts" setup>
import { EvaluationCriteria } from "@/types/EvaluationResults";
import { storeToRefs } from "pinia";
import { useChecklistControllerStore } from "~/composables/useChecklistControllerStore";
import { useDocumentHighlightStore } from "~/composables/useDocumentHighlightStore";
import { useDocumentShowFormattingStore } from "~/composables/useDocumentShowFormattingStore";
import { ChecklistCriteria, CriteriaInstance } from "~/types/Checklist";
import ReviewExplanationModal from "./ReviewExplanationModal.vue";

const { criteriaInstance, checklistCriteria } = defineProps({
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  checklistCriteria: {
    type: Object as PropType<ChecklistCriteria>,
    required: true,
  },
});

const documentId = inject("documentId") as any;

const evalutionResultsStore = useResultsForCriteriaIdStore(
  documentId,
  criteriaInstance
);

const { reevaluate, acceptEvaluation, setIsReviewedByStudent } =
  evalutionResultsStore;
const {
  evaluationResults,
  requiresInstanceReview,
  hasCorrectNumberOfBodyParagraphs,
  criteriaParagraphPositionsData,
  unallocatedInstancesForParagraph,
  relevantInstances,
  criteriaConfig,
} = storeToRefs(evalutionResultsStore);

const { clearAll } = useDocumentHighlightStore(documentId);
clearAll();

useDocumentAnnotationHighlight().clearAnnotationsToHighlight();

const checklistControllerStore = useChecklistControllerStore(documentId);
const { checklistControllerMode } = storeToRefs(checklistControllerStore);

const reviewInstances = () => {
  evalutionResultsStore.setHasReviewedInstances(false);
};

// Now we only care aobut whether or not the evaluation method is apragraph or not and whether there's a metric.
const showParagraphContainer = computed(() => {
  return criteriaInstance.evaluationMethod == "paragraph";
});

const showCriteriaContainer = computed(() => {
  return (
    checklistCriteria.evaluationMetricId != undefined &&
    criteriaInstance.evaluationMethod != "paragraph"
  );
});

const showComponentCriteriaContainer = computed(() => {
  return checklistCriteria.isAssignmentComponentCriteria == true;
});

const markReviewed = () => {
  useDocumentHighlightStore(documentId).clearAll();
  evalutionResultsStore.setHasReviewedInstances(true);
};

const clearCriteriaSettings = () => {
  documentActiveTypeStore.clearAll();
  useDocumentShowFormattingStore(documentId).setShouldShowFormatting(false);
};

const back = () => {
  if (isTeacherViewMode.value != true) {
    clearCriteriaSettings();
  }

  if (checklistControllerMode.value == ChecklistControllerMode.reviewSteps) {
    checklistControllerStore.previousStep();
  } else {
    checklistControllerStore.setCurrentChecklistCriteriaInstance(undefined);
  }
};

const acceptAndBack = async () => {
  useDocumentShowFormattingStore(documentId).setShouldShowFormatting(false);

  await acceptEvaluation(isTeacherViewMode.value == true);

  if (isTeacherViewMode.value != true) {
    setIsReviewedByStudent();
    clearCriteriaSettings();
  }

  if (checklistControllerMode.value == ChecklistControllerMode.reviewSteps) {
    checklistControllerStore.nextStep();
  } else {
    checklistControllerStore.setCurrentChecklistCriteriaInstance(undefined);
  }
};

const reportProblem = async (ev: any, isControl: boolean) => {
  const description = isControl
    ? "This is a control test that should pass."
    : undefined;

  await evalutionResultsStore.reportProblem(ev, isControl, description);
  useBaseToast("Problem reported.", "success");
};

const documentActiveTypeStore = useDocumentActiveTypeStore(documentId);

const stepContainer = ref();
const { focused } = useFocus(stepContainer);

onBeforeMount(() => {
  evalutionResultsStore.processCriteria();
});

onMounted(() => {
  if (checklistCriteria.showFormatting) {
    useDocumentShowFormattingStore(documentId).setShouldShowFormatting(true);
  }

  if (checklistCriteria.activeTypes) {
    documentActiveTypeStore.setActiveTypes(checklistCriteria.activeTypes);
  }

  if (checklistCriteria.activeSubTypes) {
    documentActiveTypeStore.setActiveSubTypes(checklistCriteria);
  }

  let comments = [] as string[];

  if (checklistCriteria.quickComments) {
    comments = [...checklistCriteria.quickComments];
  }

  focused.value = true;
});

const showOverrides = ref(false);
const toggleShowOverrides = () => {
  showOverrides.value = !showOverrides.value;
};

const appUserData = useAppUserDataStore();
const { isUserProductReviewer } = storeToRefs(appUserData);

// hmm oh I need to check that recursively don't ?
const hasFailedEvaluationResults = computed(() => {
  if (evaluationResults.value == undefined) return false;

  return evaluationResults.value?.status == EvaluationResultsStatus.failed;
});

const hasConfiguration = computed(() => {
  let hasConfigurationOptions = false;

  if (!evaluationResults.value?.evaluationResult) return false;

  const criteriaList = evaluationResults.value?.evaluationResult ?? [];

  for (const criteria of criteriaList) {
    if (checkCriteriaRecursive.value(criteria)) {
      hasConfigurationOptions = true;
    }
  }

  return hasConfigurationOptions;
});

const checkCriteriaRecursive = computed(() => {
  return (evaluationCriteria: EvaluationCriteria) => {
    if (
      Object.keys(evaluationCriteria.options).includes("values") ||
      Object.keys(evaluationCriteria.options).includes("wordsToIgnore")
    ) {
      return true;
    }

    let hasConfigurationOptions = false;

    for (const criteria of evaluationCriteria.criteria) {
      if (checkCriteriaRecursive.value(criteria)) {
        hasConfigurationOptions = true;
      }
    }

    return hasConfigurationOptions;
  };
});

const buttonLabel = computed(() => {
  const unallocatedInstances = relevantInstances.value.filter(
    (instance) => instance.bodyParagraphPosition == undefined
  );

  if (unallocatedInstances.length == relevantInstances.value.length) {
    return "Skip";
  }

  return "Done";
});

const showReviewExplanation = () => {
  const { open, close } = useDialog({
    title: "Assign Items",
    component: ReviewExplanationModal,
    props: {
      documentId: documentId,
      onClose: () => {
        close();
      },
    },
  });

  open();
};

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const documentChecklistStore = useDocumentChecklistStore(documentId);
const { useChecklist } = storeToRefs(documentChecklistStore);

const studentInstructionsToShow = computed(() => {
  if (useChecklist.value == undefined) return undefined;

  const checklistLevel = useChecklist.value.levelId;

  const overrideLvel = checklistCriteria.studentInstructionsOverrides?.find(
    (override) => override.levelIds.includes(checklistLevel)
  );

  if (overrideLvel != undefined) {
    return overrideLvel.instructions;
  }

  return checklistCriteria.studentInstructions;
});
</script>

<template>
  <div
    ref="stepContainer"
    class="flex flex-col h-full px-4 focus:outline-none"
    tabindex="9"
  >
    <div class="flex flex-row items-start w-full justify-between mb-1">
      <div class="flex flex-row items-start w-full">
        <div class="flex flex-row items-start grow">
          <BaseIconButton
            data-step-id="criteria-back-button"
            class="mr-2"
            :size="35"
            @click="back"
          >
            <icon name="material-symbols:arrow-back" size="20" />
          </BaseIconButton>
          <h2 class="text-h5 font-bold mr-2 leading-[35px]">
            {{ checklistCriteria.label }}
          </h2>
        </div>
      </div>
      <BasePopover
        v-if="
          isTeacherViewMode &&
          (checklistCriteria.evaluationMetricId ||
            hasConfiguration ||
            isUserProductReviewer)
        "
        :trigger-methods="['click']"
        align="end"
      >
        <BaseIconButton :size="35">
          <icon name="ri:more-2-fill" size="40" />
        </BaseIconButton>
        <template #content>
          <div class="p-1">
            <BasePopoverMenuItem
              v-if="checklistCriteria.evaluationMetricId"
              @click="reevaluate()"
            >
              <template #icon>
                <BaseIconRefresh class="mr-1" />
              </template>
              Reevaluate
            </BasePopoverMenuItem>
            <BasePopoverMenuItem
              v-if="hasConfiguration"
              @click="toggleShowOverrides"
            >
              <template #icon>
                <BaseIconTools class="mr-1" />
              </template>
              {{ showOverrides ? "Hide" : "Show" }} Configuration
            </BasePopoverMenuItem>
            <DocumentPanelChecklistSaveTestDocumentButton
              :document-id="documentId"
              :criteria-instance="criteriaInstance"
              :checklist-criteria="checklistCriteria"
            />
          </div>
        </template>
      </BasePopover>
    </div>
    <div
      class="@container flex flex-col grow min-h-0 overflow-auto"
      :style="{ 'container-type': 'size' }"
    >
      <BaseLabelBadge
        v-if="
          isTeacherViewMode != true &&
          studentInstructionsToShow &&
          studentInstructionsToShow != ''
        "
        class="mb-1"
      >
        <BaseTextEditor
          :min-height="10"
          :model-value="studentInstructionsToShow"
          :read-only="true"
          class="text-dark"
        />
      </BaseLabelBadge>
      <DocumentPanelChecklistScore
        v-if="isTeacherViewMode"
        class="mb-1"
        :criteria-instance="criteriaInstance"
        :checklist-criteria="checklistCriteria"
        :max-points="
          typeof criteriaInstance.maxPoints == 'string'
            ? parseInt(criteriaInstance.maxPoints)
            : criteriaInstance.maxPoints
        "
      />

      <div class="flex flex-row justify-between mb-1">
        <BaseTextButton
          v-if="
            showParagraphContainer &&
            checklistCriteria.evaluationMetricId &&
            hasCorrectNumberOfBodyParagraphs != true &&
            requiresInstanceReview != true
          "
          class="shrink-0"
          @click="reviewInstances"
        >
          Review Instances
        </BaseTextButton>
      </div>

      <TransitionsCollapsible>
        <DocumentPanelChecklistCriteriaConfiguration
          v-if="showOverrides && evaluationResults"
          class="grow overflow-auto"
          :evaluation-results="evaluationResults"
        />
      </TransitionsCollapsible>

      <!-- ok now my if statements -->
      <div
        v-if="
          requiresInstanceReview &&
          showParagraphContainer == true &&
          isTeacherViewMode
        "
        class="bg-warning-default bg-opacity-20 border border-dashed border-warning-default p-2 mb-2 rounded text-center"
      >
        <span class="text-body-1 flex flex-row items-center justify-center">
          <span> Assign each item to a paragraph. </span>
          <BaseIconButton :size="40" @click="showReviewExplanation">
            <icon name="material-symbols:help" size="30" />
          </BaseIconButton>
        </span>
      </div>

      <div v-if="hasFailedEvaluationResults" class="mb-2 flex flex-col gap-2">
        <div class="bg-danger text-danger-on p-2 rounded">
          <span class="flex flex-row items-center gap-1">
            <icon name="material-symbols:error" size="20" />
            <span> Oops! An error occurred. Click Reevaluate. </span>
          </span>
        </div>
        <BaseButton @click="reevaluate()"> Reevaluate </BaseButton>
      </div>

      <DocumentPanelChecklistParagraphContainer
        v-if="
          showParagraphContainer == true && hasFailedEvaluationResults != true
        "
        class="@min-h-[380px]:grow @min-h-[380px]:min-h-0 @min-h-[380px]:overflow-auto growable"
        data-step-id="criteria-content-container"
        :criteria-instance="criteriaInstance"
        :checklist-criteria="checklistCriteria"
        @report-problem="reportProblem"
      />
      <DocumentPanelChecklistCriteriaContainer
        v-if="
          showCriteriaContainer == true && hasFailedEvaluationResults != true
        "
        class="@min-h-[380px]:grow @min-h-[380px]:min-h-0 @min-h-[380px]:overflow-auto growable"
        data-step-id="criteria-content-container"
        :criteria-instance="criteriaInstance"
        :checklist-criteria="checklistCriteria"
        @report-problem="reportProblem"
      />
      <DocumentPanelChecklistComponentCriteriaContainer
        v-if="
          showComponentCriteriaContainer == true &&
          hasFailedEvaluationResults != true
        "
        class="@min-h-[380px]:grow @min-h-[380px]:min-h-0 @min-h-[380px]:overflow-auto growable"
        data-step-id="criteria-content-container"
        :criteria-instance="criteriaInstance"
        :checklist-criteria="checklistCriteria"
        @report-problem="reportProblem"
      />
    </div>

    <div class="mb-2 py-2 min-h-0" tabindex="100">
      <BaseButton
        data-step-id="done-review-criteria-button"
        @click="acceptAndBack()"
      >
        {{
          checklistControllerMode == ChecklistControllerMode.summary
            ? "Done"
            : "Next"
        }}
      </BaseButton>
    </div>
  </div>
</template>

<style scoped>
@container (min-height: 380px) {
  .growable {
    @apply min-h-0 overflow-auto grow;
  }
}
</style>
